import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import Loading from '../components/Loading';

const today = () => new Date().toISOString().slice(0, 10);
const PAYMENT_MODES = ['Cash', 'Bank', 'UPI', 'Cheque', 'Card', 'Other'];

// Payment Entry — Admin / Sub-Admin. Record payments and manage the entries —
// edit/delete a single entry, or select several and delete them together.
const PaymentEntry = () => {
  const [allPayments, setAllPayments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [makeOpen, setMakeOpen] = useState(false);
  const [makeForm, setMakeForm] = useState({ employee: '', type: 'payment', amount: '', date: today(), paymentMode: 'Cash', remarks: '' });
  const [makeLedger, setMakeLedger] = useState(null);

  const [editPayment, setEditPayment] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', date: '', paymentMode: 'Cash', remarks: '' });
  const [deletePayment, setDeletePayment] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const toast = useToast();

  const formatCurrency = (v) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(v) || 0);

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const loadPayments = async () => {
    try {
      const res = await axios.get('/api/payments');
      setAllPayments(res.data);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await axios.get('/api/admin/employees');
      setEmployees(res.data);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    loadPayments();
    loadEmployees();
  }, []);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.size === allPayments.length ? new Set() : new Set(allPayments.map((p) => p._id))));
  };

  const openMake = () => {
    setMakeForm({ employee: '', type: 'payment', amount: '', date: today(), paymentMode: 'Cash', remarks: '' });
    setMakeLedger(null);
    setMakeOpen(true);
  };

  const onMakeEmployee = async (e) => {
    const id = e.target.value;
    setMakeForm((f) => ({ ...f, employee: id }));
    setMakeLedger(null);
    if (id) {
      try {
        const res = await axios.get(`/api/payments/employee/${id}`);
        setMakeLedger(res.data);
      } catch (err) {
        /* ignore */
      }
    }
  };

  const submitMake = async () => {
    if (!makeForm.employee) return toast.error('Select an Employee ID');
    const amount = parseFloat(makeForm.amount);
    if (!amount || amount <= 0) return toast.error('Enter a valid amount');
    setSaving(true);
    try {
      await axios.post('/api/payments', { ...makeForm, amount });
      toast.success('Payment saved');
      setMakeOpen(false);
      setMakeLedger(null);
      await loadPayments();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Payment failed');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (p) => {
    setEditPayment(p);
    setEditForm({
      amount: p.amount,
      date: (p.date || '').slice(0, 10) || today(),
      paymentMode: p.paymentMode || 'Cash',
      remarks: p.remarks || '',
    });
  };

  const submitEdit = async () => {
    const amount = parseFloat(editForm.amount);
    if (!amount || amount <= 0) return toast.error('Enter a valid amount');
    setSaving(true);
    try {
      await axios.patch(`/api/payments/${editPayment._id}`, {
        amount, date: editForm.date, paymentMode: editForm.paymentMode, remarks: editForm.remarks,
      });
      toast.success('Payment updated');
      setEditPayment(null);
      await loadPayments();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletePayment) return;
    setSaving(true);
    try {
      await axios.delete(`/api/payments/${deletePayment._id}`);
      toast.success('Payment deleted');
      setDeletePayment(null);
      await loadPayments();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setSaving(true);
    try {
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await axios.delete(`/api/payments/${id}`);
      }
      toast.success(`${ids.length} ${ids.length === 1 ? 'entry' : 'entries'} deleted`);
      setBulkOpen(false);
      await loadPayments(); // also clears the selection
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  const makeTotals = makeLedger?.totals;
  const allSelected = allPayments.length > 0 && selectedIds.size === allPayments.length;

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Payment Entry</h1>
          <p className="page-subtitle">Payments made to employees</p>
        </div>
        <button className="btn btn-primary" onClick={openMake}>+ Make Payment</button>
      </div>

      <div className="card">
        <div className="card-body">
          {allPayments.length === 0 ? (
            <div className="empty-state">
              <h3 className="empty-state-title">No payment entries yet</h3>
              <p className="empty-state-text">Use “Make Payment” to record the first payment</p>
            </div>
          ) : (
            <>
              {selectedIds.size > 0 && (
                <div className="filters" style={{ marginBottom: '12px', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600 }}>{selectedIds.size} selected</span>
                  <button className="btn btn-danger btn-sm" onClick={() => setBulkOpen(true)} disabled={saving}>Delete Selected</button>
                  <button className="btn btn-outline btn-sm" onClick={() => setSelectedIds(new Set())}>Clear</button>
                </div>
              )}
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: '36px', textAlign: 'center' }}>
                        <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                      </th>
                      <th>Date</th>
                      <th>Employee ID</th>
                      <th>Employee Name</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th>Payment Mode</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allPayments.map((p) => (
                      <tr key={p._id}>
                        <td style={{ textAlign: 'center' }}>
                          <input type="checkbox" checked={selectedIds.has(p._id)} onChange={() => toggleSelect(p._id)} />
                        </td>
                        <td>{formatDate(p.date)}</td>
                        <td style={{ fontWeight: 600 }}>{p.employeeId || '—'}</td>
                        <td>{p.employeeName || '—'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{p.amount}</td>
                        <td>{p.paymentMode || '—'}</td>
                        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <button className="btn btn-outline btn-sm" onClick={() => openEdit(p)} style={{ marginRight: '6px' }}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => setDeletePayment(p)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Make Payment modal */}
      {makeOpen && (
        <div className="modal-overlay" onClick={() => setMakeOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Make Payment</h3>
              <button className="modal-close" onClick={() => setMakeOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Employee ID *</label>
                <select className="form-select" value={makeForm.employee} onChange={onMakeEmployee}>
                  <option value="">Select Employee ID</option>
                  {employees.map((em) => (
                    <option key={em._id} value={em._id}>{em.employeeId ? `${em.employeeId} · ` : ''}{em.name}</option>
                  ))}
                </select>
              </div>

              {makeTotals && (
                <div className="alert" style={{ fontSize: '0.82rem', lineHeight: 1.6 }}>
                  Payable {formatCurrency(makeTotals.totalPayable)} · Paid {formatCurrency(makeTotals.totalPaidByAdmin ?? makeTotals.totalPaid)} · Advance {formatCurrency(makeTotals.advancePaid)} · <strong>Remaining Payable {formatCurrency(makeTotals.remainingPayableBalance ?? makeTotals.remainingBalance)}</strong>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Amount *</label>
                <input type="number" className="form-input" step="0.01" min="0" value={makeForm.amount} onChange={(e) => setMakeForm({ ...makeForm, amount: e.target.value })} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Date</label>
                <input type="date" className="form-input" value={makeForm.date} onChange={(e) => setMakeForm({ ...makeForm, date: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Mode</label>
                <select className="form-select" value={makeForm.paymentMode} onChange={(e) => setMakeForm({ ...makeForm, paymentMode: e.target.value })}>
                  {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Remarks</label>
                <textarea className="form-textarea" placeholder="Optional remarks" value={makeForm.remarks} onChange={(e) => setMakeForm({ ...makeForm, remarks: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setMakeOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitMake} disabled={saving}>{saving ? 'Saving...' : 'Save Payment'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editPayment && (
        <div className="modal-overlay" onClick={() => setEditPayment(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Edit Payment</h3>
              <button className="modal-close" onClick={() => setEditPayment(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Employee</label>
                <input type="text" className="form-input" value={`${editPayment.employeeId || ''} · ${editPayment.employeeName || ''}`} disabled style={{ background: 'var(--gray-100)' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Amount *</label>
                <input type="number" className="form-input" step="0.01" min="0" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Date</label>
                <input type="date" className="form-input" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Mode</label>
                <select className="form-select" value={editForm.paymentMode} onChange={(e) => setEditForm({ ...editForm, paymentMode: e.target.value })}>
                  {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Remarks</label>
                <textarea className="form-textarea" value={editForm.remarks} onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setEditPayment(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitEdit} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete single entry */}
      {deletePayment && (
        <div className="modal-overlay" onClick={() => setDeletePayment(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Payment</h3>
              <button className="modal-close" onClick={() => setDeletePayment(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.95rem', lineHeight: 1.6 }}>
                Delete this payment of {formatCurrency(deletePayment.amount)} for {deletePayment.employeeId ? `${deletePayment.employeeId} · ` : ''}{deletePayment.employeeName}? The employee's payable and balance will be updated automatically.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setDeletePayment(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmDelete} disabled={saving}>Confirm Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete multiple entries */}
      {bulkOpen && (
        <div className="modal-overlay" onClick={() => setBulkOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Payments</h3>
              <button className="modal-close" onClick={() => setBulkOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.95rem', lineHeight: 1.6 }}>
                Delete <strong>{selectedIds.size}</strong> selected payment {selectedIds.size === 1 ? 'entry' : 'entries'}? The affected employees' payable and balance will be updated automatically.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setBulkOpen(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmBulkDelete} disabled={saving}>{saving ? 'Deleting...' : 'Confirm Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentEntry;
